import * as THREE from 'three';
import { ClientSim } from './sim.js';
import { Renderer } from './render.js';

interface Transient {
  obj: THREE.Object3D;
  born: number;
  ttl: number;
  update?: (age: number, t: Transient) => void;
  fromId?: number;
  toId?: number;
  kind: string;
}

/**
 * Transient combat/mining visuals. Everything is cheap geometry with
 * additive materials; positions re-anchor to the moving ships each frame.
 */
export class Vfx {
  private items: Transient[] = [];
  private tracerMat = new THREE.LineBasicMaterial({ color: 0xffc46b, transparent: true, linewidth: 2 });
  private missMat = new THREE.LineBasicMaterial({ color: 0x5a7a9a, transparent: true, opacity: 0.4 });
  private beamMat = new THREE.LineBasicMaterial({ color: 0x7bf5e0, transparent: true, opacity: 0.85 });

  constructor(private scene: THREE.Scene) {}

  private lerpRel(sim: ClientSim, id: number, alpha: number, focus: THREE.Vector3): THREE.Vector3 | null {
    const ball = sim.park.balls.get(id);
    if (!ball) return null;
    const v = new THREE.Vector3();
    Renderer.lerpPos(ball, alpha, v).sub(focus);
    return v;
  }

  spawnLine(kind: 'tracer' | 'miss' | 'beam', fromId: number, toId: number): void {
    const mat = kind === 'beam' ? this.beamMat : kind === 'tracer' ? this.tracerMat : this.missMat;
    const geom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, 0, 1)]);
    const line = new THREE.Line(geom, mat.clone());
    this.scene.add(line);
    this.items.push({
      obj: line,
      born: performance.now(),
      ttl: kind === 'beam' ? 1100 : 450,
      fromId,
      toId,
      kind,
      update: (age, t) => {
        const m = (t.obj as THREE.Line).material as THREE.LineBasicMaterial;
        m.opacity = Math.max(0, 1 - age / t.ttl) * (kind === 'beam' ? 0.85 : 1);
      },
    });
  }

  spawnShieldHit(toId: number): void {
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(1, 14, 14),
      new THREE.MeshBasicMaterial({ color: 0x6db8ff, transparent: true, opacity: 0.5, side: THREE.DoubleSide }),
    );
    this.scene.add(mesh);
    this.items.push({
      obj: mesh,
      born: performance.now(),
      ttl: 500,
      toId,
      kind: 'shieldhit',
      update: (age, t) => {
        const k = age / t.ttl;
        (t.obj as THREE.Mesh).scale.setScalar(1 + k * 0.6);
        ((t.obj as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = 0.5 * (1 - k);
      },
    });
  }

  spawnExplosion(pos: THREE.Vector3 | null, anchorId: number | undefined, size: number): void {
    const group = new THREE.Group();
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(size * 0.8, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0xffe0a3, transparent: true }),
    );
    const shell = new THREE.Mesh(
      new THREE.SphereGeometry(size * 0.5, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0xff7a3a, transparent: true, opacity: 0.8 }),
    );
    group.add(core, shell);
    // Debris sparks
    const sparks = new THREE.BufferGeometry();
    const n = 40;
    const p = new Float32Array(n * 3);
    const vel: THREE.Vector3[] = [];
    for (let i = 0; i < n; i++) {
      vel.push(new THREE.Vector3((Math.random() - 0.5), (Math.random() - 0.5), (Math.random() - 0.5)).normalize().multiplyScalar(size * (2 + Math.random() * 5)));
    }
    sparks.setAttribute('position', new THREE.BufferAttribute(p, 3));
    const points = new THREE.Points(sparks, new THREE.PointsMaterial({ color: 0xffb37a, size: Math.max(2, size * 0.12), transparent: true }));
    group.add(points);
    if (pos) group.position.copy(pos);
    this.scene.add(group);
    this.items.push({
      obj: group,
      born: performance.now(),
      ttl: 1600,
      fromId: anchorId,
      kind: 'explosion',
      update: (age, t) => {
        const k = age / t.ttl;
        core.scale.setScalar(1 + k * 3);
        (core.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.9 - k * 1.4);
        shell.scale.setScalar(1 + k * 6);
        (shell.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.8 - k);
        const arr = sparks.getAttribute('position') as THREE.BufferAttribute;
        for (let i = 0; i < n; i++) {
          arr.setXYZ(i, vel[i].x * k * 1.6, vel[i].y * k * 1.6, vel[i].z * k * 1.6);
        }
        arr.needsUpdate = true;
        (points.material as THREE.PointsMaterial).opacity = 1 - k;
      },
    });
  }

  /** Re-anchor and age everything. Call once per frame. */
  update(sim: ClientSim, now: number, focus: THREE.Vector3): void {
    const alpha = sim.alpha(now);
    for (let i = this.items.length - 1; i >= 0; i--) {
      const t = this.items[i];
      const age = now - t.born;
      if (age > t.ttl) {
        this.scene.remove(t.obj);
        this.items.splice(i, 1);
        continue;
      }
      // Anchor lines between moving ships
      if ((t.kind === 'tracer' || t.kind === 'miss' || t.kind === 'beam') && t.fromId !== undefined && t.toId !== undefined) {
        const a = this.lerpRel(sim, t.fromId, alpha, focus);
        const b = this.lerpRel(sim, t.toId, alpha, focus);
        if (a && b) {
          const geom = (t.obj as THREE.Line).geometry;
          const attr = geom.getAttribute('position') as THREE.BufferAttribute;
          attr.setXYZ(0, a.x, a.y, a.z);
          attr.setXYZ(1, b.x, b.y, b.z);
          attr.needsUpdate = true;
          geom.computeBoundingSphere();
        }
      }
      if (t.kind === 'shieldhit' && t.toId !== undefined) {
        const b = this.lerpRel(sim, t.toId, alpha, focus);
        const ball = sim.park.balls.get(t.toId);
        if (b && ball) {
          t.obj.position.copy(b);
          const r = Math.max(6, ball.radius * 1.5);
          t.obj.scale.setScalar(r * (1 + (age / t.ttl) * 0.6));
        }
      }
      if (t.kind === 'explosion' && t.fromId !== undefined) {
        const b = this.lerpRel(sim, t.fromId, alpha, focus);
        if (b) t.obj.position.copy(b); // follows wreck position if ball persists briefly
      }
      t.update?.(age, t);
    }
  }
}

/** Warp tunnel: star-streak cone around the camera while our ship warps fast. */
export class WarpTunnel {
  mesh: THREE.Mesh;
  private tex: THREE.CanvasTexture;

  constructor(scene: THREE.Scene) {
    const c = document.createElement('canvas');
    c.width = 512;
    c.height = 128;
    const g = c.getContext('2d')!;
    g.fillStyle = 'rgba(0,0,0,0)';
    g.fillRect(0, 0, 512, 128);
    for (let i = 0; i < 90; i++) {
      const y = Math.random() * 128;
      const len = 30 + Math.random() * 120;
      const x = Math.random() * 512;
      const grad = g.createLinearGradient(x, y, x + len, y);
      grad.addColorStop(0, 'rgba(140,210,255,0)');
      grad.addColorStop(0.5, `rgba(170,220,255,${0.25 + Math.random() * 0.5})`);
      grad.addColorStop(1, 'rgba(140,210,255,0)');
      g.fillStyle = grad;
      g.fillRect(x, y, len, 1.2 + Math.random());
    }
    this.tex = new THREE.CanvasTexture(c);
    this.tex.wrapS = this.tex.wrapT = THREE.RepeatWrapping;
    this.tex.repeat.set(3, 2);

    this.mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(900, 2600, 14000, 24, 1, true),
      new THREE.MeshBasicMaterial({
        map: this.tex,
        transparent: true,
        opacity: 0,
        side: THREE.BackSide,
        depthWrite: false,
      }),
    );
    this.mesh.rotation.x = Math.PI / 2;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  /** speedFrac 0..1 of warp cruise; heading = ship velocity direction. */
  update(active: boolean, speed: number, heading: THREE.Vector3, dt: number): void {
    const mat = this.mesh.material as THREE.MeshBasicMaterial;
    const target = active ? Math.min(0.75, speed / 40_000) : 0;
    mat.opacity += (target - mat.opacity) * Math.min(1, dt * 3);
    this.mesh.visible = mat.opacity > 0.02;
    if (this.mesh.visible) {
      this.tex.offset.y -= dt * (1.5 + speed / 8000);
      if (heading.lengthSq() > 1e-6) {
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), heading.clone().normalize());
        this.mesh.quaternion.copy(q);
      }
    }
  }
}
